import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  isOpen: false, // 👈 Added this to track if the modal is open
  isUpdate: false,
  updateCommunityId: null,
  originalName: '',
  step: 1,
  formData: {
    category: '',
    name: '',
    description: '',
    privacy: 'PUBLIC',
    banner: null,
    avatar: null,
    myUniversityOnly: 0,
    rules: [],
    tags: [],
  },
};

const parseRulesHelper = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const parseTagsHelper = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

export const communitySlice = createSlice({
  name: 'community',
  initialState,
  reducers: {
    // 👈 Added openModal action
    openModal: (state) => {
      state.isOpen = true;
      state.isUpdate = false;
      state.updateCommunityId = null;
      state.originalName = '';
      state.step = 1;
      state.formData = initialState.formData;
    },
    openUpdateModal: (state, action) => {
      const { communityId, communityData } = action.payload || {};
      state.isOpen = true;
      state.isUpdate = true;
      state.updateCommunityId = communityId || communityData?.id || null;
      state.originalName = communityData?.name || '';
      state.step = 1;

      let bannerVal = null;
      if (communityData?.media && Array.isArray(communityData.media) && communityData.media.length > 0) {
        const m0 = communityData.media[0];
        bannerVal = typeof m0 === 'string' ? m0 : (m0?.key || m0?.url || null);
      } else {
        bannerVal = communityData?.banner || communityData?.image || null;
      }

      const rawRules = parseRulesHelper(communityData?.rules);
      const formattedRules = rawRules.map((r, idx) => ({
        key: r.key || idx + 1,
        title: String(r.title || r.rule || "").trim(),
        description: String(r.description || r.desc || "").trim(),
      }));

      const rawTags = parseTagsHelper(communityData?.hashtags || communityData?.tags);
      const formattedTags = rawTags.map(t => typeof t === 'string' ? t.trim() : String(t).trim()).filter(Boolean);

      state.formData = {
        category: communityData?.category?.name || communityData?.category?.id || communityData?.categoryId || '',
        name: communityData?.name || '',
        description: communityData?.description || '',
        privacy: communityData?.privacyType || communityData?.privacy || 'PUBLIC',
        banner: bannerVal,
        avatar: communityData?.logo || communityData?.avatar || null,
        myUniversityOnly: communityData?.myUniversityOnly !== undefined ? Number(communityData.myUniversityOnly) : 1,
        rules: formattedRules,
        tags: formattedTags,
      };
    },
    // 👈 Added closeModal action (and resetting the form so it's fresh next time)
    closeModal: (state) => {
      state.isOpen = false;
      state.isUpdate = false;
      state.updateCommunityId = null;
      state.originalName = '';
      state.step = 1;
      state.formData = initialState.formData; 
    },
    nextStep: (state) => {
      if (state.step < 3) state.step += 1;
    },
    prevStep: (state) => {
      if (state.step > 1) state.step -= 1;
    },
    updateFormData: (state, action) => {
      // Merges incoming data with existing form data
      state.formData = { ...state.formData, ...action.payload };
    },
    resetForm: () => initialState,
  },
});

// 👈 Don't forget to export the new actions here!
export const { 
  openModal, 
  openUpdateModal,
  closeModal, 
  nextStep, 
  prevStep, 
  updateFormData, 
  resetForm 
} = communitySlice.actions;

export default communitySlice.reducer;